import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building,
  Building2,
  Phone,
  Mail,
  KeyRound,
  Pencil,
  UserCheck,
  UserX,
  ShieldAlert,
  ShieldCheck,
  User,
  Calendar,
  Clock,
  Hash,
  BarChart3,
  ClipboardList,
  Users,
  Activity,
  History,
  LogIn,
  ShieldOff,
  Shield,
  X,
  Briefcase,
  RotateCw,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { api } from "../../lib/api";
import { isSuperAdmin, isAdmin, formatRole, DEPARTMENTS, getUserCompanies, formatUserCompanies } from "../../lib/permissions";
import PasswordInput from "../ui/PasswordInput";
import MultiSelectDepartments from "../ui/MultiSelectDepartments";
import MultiSelectCompanies from "../ui/MultiSelectCompanies";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function roleColors(role) {
  const r = (role || "user").toLowerCase();
  if (r === "super_admin") return { bg: "#f3e8ff", color: "#6d28d9", border: "#c4b5fd", topBorder: "#7c3aed" };
  if (r === "admin") return { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", topBorder: "#2563eb" };
  return { bg: "#ecfdf5", color: "#047857", border: "#6ee7b7", topBorder: "#10b981" };
}

function RoleIcon({ role, size = 13 }) {
  const r = (role || "user").toLowerCase();
  if (r === "super_admin") return <ShieldAlert size={size} />;
  if (r === "admin") return <ShieldCheck size={size} />;
  return <User size={size} />;
}

function initials(name) {
  return (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function auditLabel(action = "") {
  const a = action.toUpperCase();
  if (a === "USER_DELETED") return { label: "Deleted", color: "#991b1b", bg: "#fef2f2" };
  if (a === "USER_RESTORED") return { label: "Restored", color: "#15803d", bg: "#f0fdf4" };
  if (a === "USER_DEACTIVATED") return { label: "Deactivated", color: "#b91c1c", bg: "#fef2f2" };
  if (a === "USER_ACTIVATED") return { label: "Activated", color: "#15803d", bg: "#f0fdf4" };
  if (a === "PASSWORD_RESET") return { label: "Password Reset", color: "#b45309", bg: "#fef3c7" };
  if (a === "PASSWORD_CHANGED_BY_USER") return { label: "Password Changed", color: "#1d4ed8", bg: "#eff6ff" };
  return { label: action.replace(/_/g, " "), color: "#475569", bg: "#f1f5f9" };
}

function actLabel(act) {
  const t = (act.type || act.action || "").toLowerCase();
  if (t.includes("lead")) return "Lead";
  if (t.includes("client")) return "Client";
  if (t.includes("order")) return "Order";
  if (t.includes("login")) return "Login";
  if (t.includes("password")) return "Password";
  if (t.includes("comment")) return "Note";
  if (t.includes("upload")) return "File";
  return "Activity";
}

function DeleteUserConfirmDialog({ user, onConfirm, onCancel, loading }) {
  return (
    <div className="modal-backdrop" style={{ zIndex: 99999 }} data-testid="delete-user-modal-backdrop">
      <div className="modal" style={{ width: "min(460px, 95%)" }} data-testid="delete-user-modal">
        <div className="modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef2f2", color: "#dc2626", display: "grid", placeItems: "center" }}>
              <Trash2 size={16} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>Delete User</h3>
              <small className="muted">Soft delete confirmation</small>
            </div>
          </div>
          <button className="icon-btn" onClick={onCancel} disabled={loading} style={{ padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
          <p style={{ margin: "0 0 14px", fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>
            Are you sure you want to delete this user?
          </p>

          <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>User:</span>
              <b style={{ color: "#0f172a" }}>{user?.name}</b>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>Email:</span>
              <b style={{ color: "#0f172a" }}>{user?.email}</b>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>Role / Dept:</span>
              <b style={{ color: "#0f172a" }}>{formatRole(user?.role)} · {user?.department || "Sales"}</b>
            </div>
          </div>

          <div style={{ background: "#fef2f2", borderLeft: "3px solid #ef4444", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#991b1b" }}>
            <b>Soft Delete Notice:</b> This user will be blocked from logging in and removed from active user selections. All historical tasks, leads, clients, orders, and reports will remain fully preserved.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="outline-btn" onClick={onCancel} disabled={loading} data-testid="delete-user-cancel-btn">Cancel</button>
          <button
            className="primary-btn"
            style={{ background: "#dc2626", borderColor: "#dc2626" }}
            onClick={onConfirm}
            disabled={loading}
            data-testid="delete-user-confirm-btn"
          >
            <Trash2 size={13} /> {loading ? "Deleting..." : "Delete User"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeactivateConfirmDialog({ name, onConfirm, onCancel, loading }) {
  return (
    <div className="modal-backdrop" style={{ zIndex: 99999 }}>
      <div className="modal" style={{ width: "min(420px, 95%)" }}>
        <div className="modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef2f2", color: "#dc2626", display: "grid", placeItems: "center" }}>
              <ShieldOff size={16} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>Deactivate Account</h3>
              <small className="muted">This will block CRM access immediately</small>
            </div>
          </div>
          <button className="icon-btn" onClick={onCancel} disabled={loading} style={{ padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.7, marginBottom: 18 }}>
          <p style={{ margin: "0 0 14px" }}>You are about to deactivate <b style={{ color: "#0f172a" }}>{name}</b>''s account.</p>
          <div style={{ background: "#fef9f0", border: "1px solid #fcd34d", borderRadius: 8, padding: "12px 14px", fontSize: 12 }}>
            <b style={{ color: "#92400e" }}>Immediate effects:</b>
            <ul style={{ margin: "6px 0 0", padding: "0 0 0 18px", color: "#78350f", lineHeight: 1.75 }}>
              <li>User <b>cannot log in</b> immediately</li>
              <li>Active session is <b>blocked on next request</b></li>
              <li>All data, leads, orders and history are <b>preserved</b></li>
              <li>You can <b>reactivate</b> at any time</li>
            </ul>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="outline-btn" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="primary-btn" style={{ background: "#dc2626", borderColor: "#dc2626" }} onClick={onConfirm} disabled={loading}>
            <ShieldOff size={13} /> {loading ? "Deactivating..." : "Deactivate Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ targetUser, currentUser, onClose, onSaved }) {
  const userDepts = targetUser.departments && Array.isArray(targetUser.departments) && targetUser.departments.length > 0
    ? targetUser.departments
    : [targetUser.department || "Sales"];
    
  const [formData, setFormData] = useState({
    name: targetUser.name || "",
    phone: targetUser.phone || "",
    role: (targetUser.role || "user").toLowerCase(),
    departments: userDepts,
    companies: getUserCompanies(targetUser),
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isSA = isSuperAdmin(currentUser);

  const handleDepartmentChange = (newDepts) => {
    setFormData((prev) => ({ ...prev, departments: newDepts }));
    if (newDepts && newDepts.length > 0) {
      setFormErrors((prev) => ({ ...prev, departments: null }));
    }
  };

  const handleCompanyChange = (newComps) => {
    setFormData((prev) => ({ ...prev, companies: newComps }));
    if (newComps && newComps.length > 0) {
      setFormErrors((prev) => ({ ...prev, companies: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.departments || formData.departments.length === 0) {
      errors.departments = "Please select at least one department";
    }
    if (!formData.companies || formData.companies.length === 0) {
      errors.companies = "Please select at least one company";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      if (errors.departments && errors.companies) {
        toast.error("Please select at least one department and one company");
      } else if (errors.departments) {
        toast.error(errors.departments);
      } else if (errors.companies) {
        toast.error(errors.companies);
      }
      return;
    }

    setSubmitting(true);
    try {
      const payload = { 
        name: formData.name, 
        phone: formData.phone, 
        role: formData.role, 
        departments: formData.departments,
        companies: formData.companies,
      };
      if (formData.password) payload.password = formData.password;
      await api("put", `/users/${targetUser.user_id}`, payload);
      toast.success("User updated successfully");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 99999 }}>
      <form
        className="modal"
        onSubmit={handleSubmit}
        style={{ width: "min(620px, 95%)", maxWidth: 620, padding: "22px 24px", overflow: "visible" }}
      >
        <div className="modal-head" style={{ marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
          <div>
            <h3 style={{ margin: "0 0 3px", fontSize: 17, color: "var(--navy)", fontWeight: 700 }}>Edit Team Member</h3>
            <small className="muted" style={{ fontSize: 12 }}>{targetUser.email} · {targetUser.user_id}</small>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} style={{ padding: 6 }}><X size={18} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <div>
            <label className="dispatch-label">Full Name <span style={{ color: "var(--red)" }}>*</span></label>
            <input
              type="text"
              className="dispatch-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="dispatch-label">Phone Number</label>
            <input
              type="text"
              className="dispatch-control"
              placeholder="e.g. +91 98200 12345"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <div>
            <label className="dispatch-label">System Role <span style={{ color: "var(--red)" }}>*</span></label>
            <select
              className="dispatch-control"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="user">USER</option>
              <option value="admin">ADMIN</option>
              {isSA && <option value="super_admin">SUPER ADMIN</option>}
            </select>
          </div>

          <div>
            <label className="dispatch-label">Reset Password (Optional)</label>
            <PasswordInput
              className="dispatch-control"
              placeholder="Leave blank to keep unchanged"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              minLength={6}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="dispatch-label">Assigned Departments <span style={{ color: "var(--red)" }}>*</span></label>
            <MultiSelectDepartments
              value={formData.departments}
              onChange={handleDepartmentChange}
              departments={DEPARTMENTS}
              required={true}
              placeholder="Select departments..."
              hasError={!!formErrors.departments}
              testId="edit-user-detail-dept-select"
            />
            {formErrors.departments && (
              <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                {formErrors.departments}
              </span>
            )}
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="dispatch-label">Assigned Companies <span style={{ color: "var(--red)" }}>*</span></label>
            <MultiSelectCompanies
              value={formData.companies}
              onChange={handleCompanyChange}
              required={true}
              placeholder="Select companies..."
              hasError={!!formErrors.companies}
              testId="edit-user-detail-company-select"
            />
            {formErrors.companies && (
              <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                {formErrors.companies}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-btn" disabled={submitting}>{submitting ? "Saving..." : "Save Changes"}</button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal({ targetUser, onClose }) {
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await api("post", `/users/${targetUser.user_id}/reset-password`);
      toast.success(res.data?.message || "Password reset email sent");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 99999 }}>
      <div className="modal" style={{ width: "min(460px, 95%)" }}>
        <div className="modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef3c7", color: "#b45309", display: "grid", placeItems: "center" }}>
              <KeyRound size={16} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>Reset Password</h3>
              <small className="muted">Administrative password reset</small>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} disabled={resetting} style={{ padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
          <p style={{ margin: "0 0 14px" }}>
            A secure temporary password will be generated and emailed to <b style={{ color: "#0f172a" }}>{targetUser.email}</b>. The user must change it on next login.
          </p>
          <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "12px 14px", marginBottom: 12, fontSize: 12 }}>
            {[["Name:", targetUser.name], ["User ID:", targetUser.user_id], ["Email:", targetUser.email], ["Role:", (targetUser.role || "").toUpperCase()]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                <span style={{ color: "#475569", width: 70, fontWeight: 600 }}>{k}</span>
                <b style={{ color: "#0f172a", fontFamily: k === "User ID:" ? "monospace" : undefined }}>{v}</b>
              </div>
            ))}
          </div>
          <div style={{ background: "#fef3c7", borderLeft: "3px solid #f59e0b", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#78350f" }}>
            <b>Security notice:</b> The temporary password is delivered only to the user''s registered email and is never shown on screen.
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="outline-btn" onClick={onClose} disabled={resetting}>Cancel</button>
          <button className="primary-btn" style={{ background: "#b45309", borderColor: "#b45309" }} onClick={handleReset} disabled={resetting}>
            <KeyRound size={13} /> {resetting ? "Sending..." : "Confirm & Send Reset Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserDetail({ userId, onBack, currentUser, onUserUpdated }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [restoringUser, setRestoringUser] = useState(false);

  const canAdminister = isAdmin(currentUser);
  const isSA = isSuperAdmin(currentUser);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("get", `/users/${userId}`);
      setUserData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load user details");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const isDeleted = Boolean(userData?.is_deleted);
  const isPrimarySuperAdmin = (userData?.email || "").toLowerCase() === "admin@pinakisolutions.com";
  const isSelf = userData?.user_id === currentUser?.user_id;

  const canModify = userData && (isSA || (canAdminister && (userData.role || "user").toLowerCase() !== "super_admin"));
  const canToggleStatus = canModify && !isSelf && !isDeleted;
  const canDelete = canModify && !isSelf && !isPrimarySuperAdmin && !isDeleted;
  const canRestore = canModify && isDeleted;
  const isActive = userData?.is_active !== false && !isDeleted;

  const handleToggleStatus = async () => {
    if (!userData) return;
    setTogglingStatus(true);
    try {
      const res = await api("post", `/users/${userData.user_id}/toggle-status`);
      setUserData((prev) => ({ ...prev, is_active: res.data.is_active }));
      toast.success(res.data.message);
      if (onUserUpdated) onUserUpdated();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update account status");
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userData) return;
    setDeletingUser(true);
    try {
      const res = await api("delete", `/users/${userData.user_id}`);
      toast.success(res.data?.message || `User '${userData.name}' soft-deleted successfully`);
      setShowDeleteConfirm(false);
      if (onUserUpdated) onUserUpdated();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleRestoreUser = async () => {
    if (!userData) return;
    setRestoringUser(true);
    try {
      const res = await api("post", `/users/${userData.user_id}/restore`);
      toast.success(res.data?.message || `User '${userData.name}' restored successfully`);
      if (onUserUpdated) onUserUpdated();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore user");
    } finally {
      setRestoringUser(false);
    }
  };

  if (loading && !userData) {
    return (
      <div className="content">
        <div className="loading-screen" style={{ minHeight: "60vh" }}>Loading user...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="content detail-content">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Users & Roles
        </button>
        <div className="panel" style={{ padding: 40, textAlign: "center", marginTop: 20 }}>
          <h3>User not found</h3>
          <p className="muted">This user may have been removed.</p>
        </div>
      </div>
    );
  }

  const rc = roleColors(userData.role);
  const activities = userData.recent_activities || [];
  const auditLogs = userData.audit_logs || [];

  return (
    <div className="content detail-content" data-testid="user-detail-page">

      <button className="back-btn" onClick={onBack} data-testid="user-detail-back-btn">
        <ArrowLeft size={14} /> Back to Users & Roles
      </button>

      <div className="detail-head">
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 14,
            background: isDeleted ? "#e2e8f0" : rc.bg,
            color: isDeleted ? "#64748b" : rc.color,
            display: "grid", placeItems: "center",
            fontSize: 22, fontWeight: 800,
            border: `2px solid ${isDeleted ? "#cbd5e1" : rc.border}`,
            flexShrink: 0,
          }}>
            {initials(userData.name)}
          </div>
          <div>
            <div className="small-label">USER / {userData.user_id}</div>
            <h1 style={{ margin: "4px 0 6px", textDecoration: isDeleted ? "line-through" : "none", color: isDeleted ? "#64748b" : undefined }}>{userData.name}</h1>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: rc.bg, color: rc.color, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5 }}>
                <RoleIcon role={userData.role} size={11} /> {formatRole(userData.role)}
              </span>
              {(() => {
                const depts = userData.departments && Array.isArray(userData.departments) && userData.departments.length > 0
                  ? userData.departments
                  : [userData.department || "General"];
                return depts.map((dept, idx) => (
                  <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 5 }}>
                    <Building2 size={11} /> {dept}
                  </span>
                ));
              })()}
              {getUserCompanies(userData).map((comp, idx) => (
                <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f8fafc", color: "#1e293b", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 5, border: "1px solid #cbd5e1" }}>
                  <Building size={11} /> {comp}
                </span>
              ))}
              {isDeleted ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#fef2f2", color: "#991b1b", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5, border: "1px solid #fecaca" }} data-testid="user-status-deleted">
                  <Trash2 size={11} /> Deleted
                </span>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: isActive ? "#f0fdf4" : "#fef2f2", color: isActive ? "#15803d" : "#b91c1c", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5 }}>
                  {isActive ? <UserCheck size={11} /> : <UserX size={11} />}
                  {isActive ? "Active" : "Inactive / Deactivated"}
                </span>
              )}
            </div>
          </div>
        </div>

        {canModify && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {isDeleted ? (
              <button
                className="outline-btn"
                style={{ color: "#15803d", borderColor: "#86efac" }}
                onClick={handleRestoreUser}
                disabled={restoringUser}
                data-testid="user-detail-restore-btn"
              >
                <RotateCcw size={13} /> {restoringUser ? "Restoring..." : "Restore User"}
              </button>
            ) : (
              <>
                <button className="outline-btn" onClick={() => setShowEdit(true)} data-testid="user-detail-edit-btn">
                  <Pencil size={13} /> Edit User
                </button>
                <button className="outline-btn" style={{ color: "#b45309", borderColor: "#fcd34d" }} onClick={() => setShowReset(true)} data-testid="user-detail-reset-pwd-btn">
                  <KeyRound size={13} /> Reset Password
                </button>
                {canToggleStatus && (
                  isActive ? (
                    <button className="outline-btn danger" onClick={() => setShowDeactivateConfirm(true)} disabled={togglingStatus} data-testid="user-detail-deactivate-btn">
                      <ShieldOff size={13} /> {togglingStatus ? "Updating..." : "Deactivate Account"}
                    </button>
                  ) : (
                    <button className="outline-btn" style={{ color: "#15803d", borderColor: "#86efac" }} onClick={handleToggleStatus} disabled={togglingStatus} data-testid="user-detail-activate-btn">
                      <Shield size={13} /> {togglingStatus ? "Updating..." : "Activate Account"}
                    </button>
                  )
                )}
                {canDelete && (
                  <button
                    className="outline-btn danger"
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="user-detail-delete-btn"
                  >
                    <Trash2 size={13} /> Delete User
                  </button>
                )}
              </>
            )}
            <button className="outline-btn" style={{ color: "#64748b" }} onClick={load} title="Refresh"><RotateCw size={13} /></button>
          </div>
        )}
      </div>

      {isDeleted ? (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 8, padding: "14px 18px", marginBottom: 22, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 13, color: "#991b1b" }} data-testid="user-deleted-banner">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Trash2 size={18} style={{ flexShrink: 0, color: "#dc2626" }} />
            <div>
              <b>Account Soft-Deleted</b> — This user account has been soft-deleted and cannot log in.
              {userData.deleted_at && (
                <span style={{ color: "#b91c1c", marginLeft: 6 }}>
                  Deleted on {fmtDateTime(userData.deleted_at)}{userData.deleted_by_name && ` by ${userData.deleted_by_name}`}.
                </span>
              )}
              <div style={{ fontSize: 11.5, color: "#7f1d1d", marginTop: 3 }}>
                All historical tasks, leads, clients, orders, and reports remain intact and accessible in CRM history.
              </div>
            </div>
          </div>
          {canRestore && (
            <button
              className="outline-btn"
              style={{ padding: "6px 12px", fontSize: 12, color: "#15803d", borderColor: "#86efac", background: "#fff", flexShrink: 0 }}
              onClick={handleRestoreUser}
              disabled={restoringUser}
              data-testid="user-banner-restore-btn"
            >
              <RotateCcw size={13} /> Restore Account
            </button>
          )}
        </div>
      ) : !isActive ? (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 8, padding: "14px 18px", marginBottom: 22, display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "#991b1b" }}>
          <ShieldOff size={18} style={{ flexShrink: 0, color: "#dc2626" }} />
          <div>
            <b>Account Deactivated</b> — This user cannot log in or access the CRM.
            {userData.deactivated_at && (
              <span style={{ color: "#b91c1c", marginLeft: 6 }}>
                Deactivated on {fmtDateTime(userData.deactivated_at)}{userData.status_changed_by && ` by ${userData.status_changed_by}`}.
              </span>
            )}
          </div>
        </div>
      ) : null}

      {/* Department-Aware Work Cards */}
      {userData.department_work && userData.department_work.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
          gap: 14,
          marginBottom: 22,
        }}>
          {userData.department_work.map((card, idx) => (
            <div
              key={idx}
              className="metric"
              style={{
                borderTopColor: card.color || "var(--blue)",
                background: "#fff",
              }}
              data-testid={`work-metric-${idx}`}
            >
              <div className="metric-top">
                <span>{card.label}</span>
              </div>
              <strong style={{ fontSize: 26, margin: "10px 0 2px" }}>{card.value ?? 0}</strong>
              <small>{card.subtext || "Records"}</small>
            </div>
          ))}
        </div>
      )}

      <div className="detail-grid" style={{ gridTemplateColumns: "1.3fr 0.7fr" }}>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <section className="panel">
            <div className="panel-title">
              <div><h3>Contact & Identity</h3><p className="muted">User profile information — no passwords shown.</p></div>
              <span className="secure-label">SECURE RECORD</span>
            </div>
            <div className="facts" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
              <div><small>User ID</small><b style={{ fontFamily: "monospace", fontSize: 12 }}>{userData.user_id}</b></div>
              <div><small>Email</small><b>{userData.email}</b></div>
              <div><small>Phone</small><b>{userData.phone || "—"}</b></div>
              <div>
                <small>Departments</small>
                <b>
                  {(() => {
                    const depts = userData.departments && Array.isArray(userData.departments) && userData.departments.length > 0
                      ? userData.departments
                      : [userData.department || "General"];
                    return depts.join(" · ");
                  })()}
                </b>
              </div>
              <div>
                <small>Companies</small>
                <b>{getUserCompanies(userData).join(" · ")}</b>
              </div>
              <div><small>Role</small><b>{formatRole(userData.role)}</b></div>
              <div><small>Account Status</small><b style={{ color: isDeleted ? "#991b1b" : isActive ? "#15803d" : "#b91c1c" }}>{isDeleted ? "Soft-Deleted" : isActive ? "Active" : "Inactive / Deactivated"}</b></div>
              <div><small>Last Login</small><b>{userData.last_login_at ? fmtDateTime(userData.last_login_at) : "Never logged in"}</b></div>
              <div><small>Created By</small><b>{userData.created_by || "System"}</b></div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-title">
              <div><h3>Recent Activity</h3><p className="muted">Last 10 actions performed by this user.</p></div>
            </div>
            {activities.length === 0 ? (
              <div className="empty-inline">No activity recorded for this user yet.</div>
            ) : (
              <div>
                {activities.map((act, i) => (
                  <div key={i} className="history-row">
                    <div className="history-icon"><Activity size={12} /></div>
                    <div style={{ flex: 1 }}>
                      <b style={{ fontSize: 12.5 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, background: "#f1f5f9", color: "#64748b", padding: "2px 6px", borderRadius: 4, marginRight: 7 }}>{actLabel(act)}</span>
                        {act.description || act.message || act.action || "Activity recorded"}
                      </b>
                      <p>{fmtDateTime(act.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel-title">
              <div><h3>Admin Audit History</h3><p className="muted">Administrative actions taken on this account.</p></div>
            </div>
            {auditLogs.length === 0 ? (
              <div className="empty-inline">No audit history found for this account.</div>
            ) : (
              <div>
                {auditLogs.map((log, i) => {
                  const { label, color, bg } = auditLabel(log.action);
                  return (
                    <div key={i} className="history-row">
                      <div className="history-icon" style={{ background: bg, color }}><History size={12} /></div>
                      <div style={{ flex: 1 }}>
                        <b style={{ fontSize: 12.5 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, background: bg, color, padding: "2px 6px", borderRadius: 4, marginRight: 7 }}>{label}</span>
                          {log.details || `Action by ${log.admin_name || "administrator"}`}
                        </b>
                        <p>{log.admin_name && <span style={{ color: "#64748b", fontWeight: 600 }}>{log.admin_name} · </span>}{fmtDateTime(log.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <section className="panel">
            <div className="panel-title"><div><h3>Account Timeline</h3><p className="muted">Key lifecycle dates.</p></div></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { label: "Date Joined", value: fmtDate(userData.created_at) },
                { label: "Last Login", value: userData.last_login_at ? fmtDateTime(userData.last_login_at) : "—" },
                { label: "Last Updated", value: userData.updated_at ? fmtDateTime(userData.updated_at) : "—" },
                { label: "Account Status", value: <span style={{ color: isDeleted ? "#991b1b" : isActive ? "#15803d" : "#b91c1c", fontWeight: 700 }}>{isDeleted ? "Soft-Deleted" : isActive ? "Active" : "Inactive / Deactivated"}</span> },
                ...(userData.deleted_at && isDeleted ? [{ label: "Deleted On", value: fmtDateTime(userData.deleted_at) }] : []),
                ...(userData.restored_at && !isDeleted ? [{ label: "Restored On", value: fmtDateTime(userData.restored_at) }] : []),
                ...(userData.deactivated_at && !isActive && !isDeleted ? [{ label: "Deactivated On", value: fmtDateTime(userData.deactivated_at) }] : []),
                ...(userData.activated_at && isActive && userData.deactivated_at ? [{ label: "Reactivated On", value: fmtDateTime(userData.activated_at) }] : []),
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                  <span style={{ color: "#475569", fontSize: 11.5, width: 110, flexShrink: 0, fontWeight: 600 }}>{label}</span>
                  <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="panel" style={{ borderTop: `3px solid ${rc.topBorder}` }}>
            <div className="panel-title">
              <div>
                <h3>Role & Access</h3>
                <p className="muted">System role and assigned departments</p>
              </div>
              {canModify && !isDeleted && (
                <button 
                  className="outline-btn compact" 
                  onClick={() => setShowEdit(true)}
                  style={{ fontSize: 11, height: 28, padding: "0 10px" }}
                >
                  <Pencil size={11} /> Edit Access
                </button>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <small style={{ display: "block", color: "#475569", fontSize: 10.5, marginBottom: 8, fontWeight: 700, letterSpacing: "0.5px" }}>SYSTEM ROLE</small>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: rc.bg, color: rc.color, fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 7 }}>
                  <RoleIcon role={userData.role} size={14} /> {formatRole(userData.role)}
                </span>
              </div>
              <div>
                <small style={{ display: "block", color: "#475569", fontSize: 10.5, marginBottom: 8, fontWeight: 700, letterSpacing: "0.5px" }}>DEPARTMENTS</small>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(() => {
                    const depts = userData.departments && Array.isArray(userData.departments) && userData.departments.length > 0
                      ? userData.departments
                      : [userData.department || "General"];
                    return depts.map((dept, idx) => (
                      <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#f1f5f9", color: "#334155", fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 7, border: "1px solid #e2e8f0" }}>
                        <Building2 size={13} /> {dept}
                      </span>
                    ));
                  })()}
                </div>
              </div>
              {userData.must_change_password && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 7, padding: "10px 14px", fontSize: 12, color: "#92400e" }}>
                  Password change required on next login.
                </div>
              )}
            </div>
          </section>

          <div style={{ background: "#f8fafc", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 16px", fontSize: 11.5, color: "#334155", lineHeight: 1.65 }}>
            <b style={{ display: "block", color: "#1e293b", marginBottom: 4 }}>Privacy & Security</b>
            Passwords and sensitive authentication data are never displayed on this page. All admin actions are recorded in the audit history above.
          </div>
        </div>
      </div>

      {showEdit && canModify && (
        <EditUserModal targetUser={userData} currentUser={currentUser} onClose={() => setShowEdit(false)} onSaved={load} />
      )}
      {showReset && canModify && (
        <ResetPasswordModal targetUser={userData} onClose={() => setShowReset(false)} />
      )}
      {showDeactivateConfirm && canToggleStatus && (
        <DeactivateConfirmDialog
          name={userData.name}
          onConfirm={() => { setShowDeactivateConfirm(false); handleToggleStatus(); }}
          onCancel={() => setShowDeactivateConfirm(false)}
          loading={togglingStatus}
        />
      )}
      {showDeleteConfirm && canDelete && (
        <DeleteUserConfirmDialog
          user={userData}
          onConfirm={handleDeleteUser}
          onCancel={() => setShowDeleteConfirm(false)}
          loading={deletingUser}
        />
      )}
    </div>
  );
}
